import type { TagComparator } from '../../graphqlEnums'
import { toUTCDayEnd, toUTCDayStart } from '../../hooks/toUTCDayBounds'

/**
 * Comparison dates are both-or-neither: a partial range is silently dropped at
 * the request layer, so modelling it as a union makes a lone date a compile
 * error rather than a masked runtime bug.
 */
type ComparisonRange =
  | { comparisonStartDate: Date; comparisonEndDate: Date }
  | { comparisonStartDate?: undefined; comparisonEndDate?: undefined }

/**
 * Shared options for the All Channels insights queries. Both
 * `useAllChannelsInsightsSummary` and `useAllChannelsTimeSeries` take the same
 * filter inputs and build an identical `input`, so they're defined here once.
 */
export type AllChannelsInsightsOptions = {
  organizationId: string
  startDate: Date
  endDate: Date
  channelIds?: string[]
  tags?: TagComparator
  /** Skip the query entirely (e.g. the actor can't view insights here). */
  skip?: boolean
} & ComparisonRange

/**
 * Build the `AllChannelsPostInsightsInput` shared by both All Channels insights
 * queries: dates serialized as inclusive UTC calendar-day bounds (see
 * {@link toUTCDayStart}/{@link toUTCDayEnd}), with channel/tag/comparison
 * filters included only when present.
 */
export function buildAllChannelsInsightsInput({
  organizationId,
  startDate,
  endDate,
  channelIds,
  tags,
  comparisonStartDate,
  comparisonEndDate,
}: AllChannelsInsightsOptions): {
  organizationId: string
  startDate: string
  endDate: string
  channelIds?: string[]
  tags?: TagComparator
  comparisonStartDate?: string
  comparisonEndDate?: string
} {
  const hasComparison = !!(comparisonStartDate && comparisonEndDate)
  return {
    organizationId,
    startDate: toUTCDayStart(startDate),
    endDate: toUTCDayEnd(endDate),
    ...(channelIds?.length ? { channelIds } : {}),
    ...(tags ? { tags } : {}),
    ...(hasComparison
      ? {
          comparisonStartDate: toUTCDayStart(comparisonStartDate),
          comparisonEndDate: toUTCDayEnd(comparisonEndDate),
        }
      : {}),
  }
}

/**
 * The query can only run once we have an org id and a date range. While those
 * prerequisites are still resolving the query is skipped, which Apollo reports
 * as `loading: false`. Treat that window as loading so consumers render their
 * loading state instead of briefly flashing an empty/no-data state on first
 * paint.
 */
export function isWaitingForInsightsPrerequisites({
  organizationId,
  startDate,
  endDate,
}: AllChannelsInsightsOptions): boolean {
  return !organizationId || !startDate || !endDate
}
